/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { INPUT_APPLICATION_ID, TriggerLabel } from "../../utils/constants";
import type {
  Application,
  IntegrationDescription,
  Member,
  Team,
} from "@smartsuite/automation-engine-api";
import { findInputAndPick, pickValueConfigString } from "../../utils/inputs";
import * as R from "remeda";
import { conditionsFormatters } from "./conditions";
import type { TriggerCandidate } from "../../types/candidate";

type TriggerFormatter = (
  trigger: TriggerCandidate,
  integrations: IntegrationDescription[],
  applications: Application[],
  members: Member[],
  teams: Team[],
  /* eslint-disable  @typescript-eslint/no-explicit-any */
) => { [key: string]: any } | string;

const isTriggerLabel = (triggerId: string): triggerId is TriggerLabel =>
  Object.values(TriggerLabel).includes(triggerId as TriggerLabel);

const getConditionText = (
  trigger: TriggerCandidate,
  members: Member[],
  teams: Team[],
  /* eslint-disable  @typescript-eslint/no-explicit-any */
): string | { [key: string]: any } => {
  if (R.isTruthy(trigger.conditions)) {
    const conditionFormatter =
      conditionsFormatters[trigger.conditions.operator];
    if (R.isTruthy(conditionFormatter)) {
      return conditionFormatter(trigger, members, teams);
    }
  }
  return "";
};

// SmartSuite trigger formatter
export const smartsuiteFormatter: TriggerFormatter = (
  trigger,
  integrations,
  applications,
  members,
  teams,
) => {
  let applicationLabel = "";
  const triggerId = trigger.triggerReference!.triggerId;
  const applicationId = findInputAndPick(
    trigger.inputs,
    INPUT_APPLICATION_ID,
    pickValueConfigString,
  );
  const currentApplication: Application | undefined = applications.find(
    (app) => app.id === applicationId,
  );
  if (currentApplication !== undefined) {
    applicationLabel = currentApplication.label;
  }

  const conditionsText = getConditionText(trigger, members, teams);

  if (isTriggerLabel(triggerId)) {
    if (triggerId === TriggerLabel.createdRecord) {
      return {
        key: "phrase-builder.when-a-record-is-created-in",
        data: { applicationLabel, conditionsText },
      };
    }
    if (triggerId === TriggerLabel.updatedRecord) {
      return {
        key: "phrase-builder.when-a-record-is-updated-in",
        data: { applicationLabel, conditionsText },
      };
    }
    if (triggerId === TriggerLabel.matchesCondition) {
      return {
        key: "phrase-builder.when-a-record-is-matches-condition",
        data: { applicationLabel, conditionsText },
      };
    }
    if (triggerId === TriggerLabel.formSubmitted) {
      return {
        key: "phrase-builder.when-a-form-is-submitted-in",
        data: { applicationLabel, conditionsText },
      };
    }
    // TODO add schedule description
    // if (triggerId === TriggerLabel.schedule) {
    // }
  }
  return otherFormatter(trigger, integrations, applications, members, teams);
};

export const otherFormatter: TriggerFormatter = (
  trigger: TriggerCandidate,
  integrations,
  applications,
  members,
  teams,
) => {
  let conditionsText: string | { [key: string]: any } = "";
  const triggerLabel = trigger.description.label;
  const integrationId = trigger.triggerReference!.integrationId;
  const integrationLabel =
    integrations.find(
      (integration) => integration.integrationId === integrationId,
    )?.label ?? integrationId;

  if (R.isTruthy(trigger.conditions)) {
    const conditionFormatter =
      conditionsFormatters[trigger.conditions.operator];
    if (R.isTruthy(conditionFormatter)) {
      conditionsText = conditionFormatter(trigger, members, teams);
    }
  }

  return {
    key: "phrase-builder.when-other-integrations",
    data: {
      integrationId: integrationLabel,
      triggerId: triggerLabel,
      conditionsText,
    },
  };
};
